'use client';

import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useSyncExternalStore } from 'react';
import { CartProduct } from '@/types/product';
import { PackageWithImage } from '@/lib/package-utils';

// Minimal product shape shared with ProductCard (full DB rows are assignable to it)
type Product = CartProduct;

// Define the cart item type with complete package data
export type CartItem = {
  product: Product;
  quantity: number;
  packageId?: number; // Optional package ID for package-based products
  packageData?: PackageWithImage; // Complete package information
  notes?: string;
};

// Define the context type
type CartContextType = {
  items: CartItem[];
  addItem: (product: Product, quantity: number, packageData?: PackageWithImage, notes?: string) => void;
  removeItem: (productId: number, packageId?: number) => void;
  updateItemQuantity: (productId: number, quantity: number, packageId?: number) => void;
  updateItemNotes: (productId: number, notes: string, packageId?: number) => void;
  clearItems: () => void;
  itemCount: number;
  isInCart: (productId: number, packageId?: number) => boolean;
  total: number;
};

// Create the context with default values
const CartContext = createContext<CartContextType>({
  items: [],
  addItem: () => {},
  removeItem: () => {},
  updateItemQuantity: () => {},
  updateItemNotes: () => {},
  clearItems: () => {},
  itemCount: 0,
  isInCart: () => false,
  total: 0,
});

// Local storage key
const STORAGE_KEY = 'cart_items';
const SERVER_SNAPSHOT: CartItem[] = []; // Define stable server snapshot

// Client-side cache for snapshot
let lastRawValue: string | null = null;
let cachedSnapshot: CartItem[] = [];

// --- Store Logic (outside component) ---

// Function to get cart from localStorage (with client-side caching)
function getCartSnapshot(): CartItem[] {
  if (typeof window === 'undefined') {
    return SERVER_SNAPSHOT; // Server snapshot
  }
  try {
    const currentRawValue = localStorage.getItem(STORAGE_KEY);
    
    // If raw value hasn't changed, return cached snapshot
    if (currentRawValue === lastRawValue) {
      return cachedSnapshot;
    }

    // Raw value changed, update cache
    lastRawValue = currentRawValue;
    let rawItems: CartItem[] = currentRawValue ? JSON.parse(currentRawValue) : [];
    
    console.log('[CartContext] Loaded cart items from localStorage:', rawItems);
    cachedSnapshot = rawItems;
    console.log('[CartStore] Snapshot updated:', cachedSnapshot.length + ' items'); 
    console.log('[CartStore] Cart items:', cachedSnapshot.map(item => ({
      productId: item.product?.id,
      productName: item.product?.name,
      packageId: item.packageId,
      hasPackageData: !!item.packageData,
      quantity: item.quantity
    })));
    return cachedSnapshot;

  } catch (error) {
    console.error('[CartStore] Error reading localStorage for snapshot:', error);
    // Reset cache on error? Or return last known good? Returning empty for now.
    lastRawValue = null; 
    cachedSnapshot = [];
    return cachedSnapshot;
  }
}

// Function to save cart to localStorage
function saveCart(items: CartItem[]) {
  try {
    console.log('[CartStore] Saving to localStorage:', items.map(item => ({
      productId: item.product.id,
      productName: item.product.name,
      packageId: item.packageId,
      quantity: item.quantity,
      productPrice: item.product.price_ars
    })));
    const newRawValue = JSON.stringify(items);
    localStorage.setItem(STORAGE_KEY, newRawValue);
    // Update cache immediately after saving
    lastRawValue = newRawValue;
    cachedSnapshot = items;
  } catch (error) {
    console.error('[CartStore] Error writing localStorage:', error);
  }
}

// Simple subscription mechanism
let listeners: (() => void)[] = [];

function emitChange() {
  console.log('[CartStore] Emitting change to', listeners.length, 'listeners');
  // Force next microtask to allow time for localStorage to settle
  setTimeout(() => {
    for (const listener of listeners) {
      listener();
    }
  }, 0);
}

function subscribe(listener: () => void): () => void {
  listeners = [...listeners, listener];
  return () => {
    listeners = listeners.filter(l => l !== listener);
  };
}

// Cart manipulation functions that update localStorage and notify listeners
const cartStore = {
  addItem: (product: Product, quantity: number, packageData?: PackageWithImage, notes?: string) => {
    const packageId = packageData?.id;
    console.log('[CartStore] Adding item:', product.name, 'quantity:', quantity, 'packageId:', packageId, 'packageData:', packageData ? 'provided' : 'none', 'notes:', notes);
    const currentItems = getCartSnapshot();
    // For packages, match by both product ID and package ID
    const existingItemIndex = currentItems.findIndex(item => 
      item.product.id === product.id && item.packageId === packageId
    );
    let updatedItems;
    if (existingItemIndex >= 0) {
      updatedItems = [...currentItems];
      updatedItems[existingItemIndex] = {
        ...updatedItems[existingItemIndex],
        quantity: updatedItems[existingItemIndex].quantity + quantity,
        notes: notes || updatedItems[existingItemIndex].notes,
        // Update package data if provided
        ...(packageData && { packageData })
      };
    } else {
      updatedItems = [...currentItems, { product, quantity, packageId, packageData, notes }];
    }
    saveCart(updatedItems);
    emitChange();
  },
  removeItem: (productId: number, packageId?: number) => {
    const currentItems = getCartSnapshot();
    const updatedItems = currentItems.filter(item => 
      !(item.product.id === productId && 
        (packageId ? item.packageId === packageId : item.packageId === undefined))
    );
    saveCart(updatedItems);
    emitChange();
  },
  updateItemQuantity: (productId: number, quantity: number, packageId?: number) => {
    if (quantity <= 0) {
      cartStore.removeItem(productId, packageId);
      return;
    }
    const currentItems = getCartSnapshot();
    const updatedItems = currentItems.map(item => 
      item.product.id === productId && item.packageId === packageId
        ? { ...item, quantity } 
        : item
    );
    saveCart(updatedItems);
    emitChange();
  },
  updateItemNotes: (productId: number, notes: string, packageId?: number) => {
    const currentItems = getCartSnapshot();
    const updatedItems = currentItems.map(item => 
      item.product.id === productId && item.packageId === packageId
        ? { ...item, notes } 
        : item
    );
    saveCart(updatedItems);
    emitChange();
  },
  clearItems: () => {
    saveCart([]);
    emitChange();
  },
  getSnapshot: getCartSnapshot, // Expose snapshot function
  subscribe: subscribe // Expose subscribe function
};

// --- React Context --- 

export function CartProvider({ children }: { children: ReactNode }) {
  // useSyncExternalStore now uses the cached getSnapshot
  const items = useSyncExternalStore(
    cartStore.subscribe,
    cartStore.getSnapshot, // This now returns cached value when appropriate
    () => SERVER_SNAPSHOT 
  );

  // Derived state calculations - updated for package support
  const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);
  
  const total = items.reduce((sum, item) => {
    let price = 0;
    
    // Priority 1: Package price if we have package data
    if (item.packageData?.price_ars) {
      price = parseFloat(String(item.packageData.price_ars));
    } 
    // Priority 2: Product ARS price
    else if (item.product.price_ars !== undefined) {
      price = parseFloat(String(item.product.price_ars));
    } 
    // Priority 3: Product USD price (fallback)
    else if (item.product.price) {
      price = parseFloat(String(item.product.price));
    }
    
    return sum + price * item.quantity;
  }, 0);
  
  const isInCart = useCallback((productId: number, packageId?: number) => {
    return items.some(item => 
      item.product.id === productId && 
      (packageId ? item.packageId === packageId : item.packageId === undefined)
    );
  }, [items]);

  // Provide the state and the manipulation functions from the store
  const value = {
    items,
    addItem: cartStore.addItem,
    removeItem: cartStore.removeItem,
    updateItemQuantity: cartStore.updateItemQuantity,
    updateItemNotes: cartStore.updateItemNotes,
    clearItems: cartStore.clearItems,
    itemCount,
    isInCart,
    total,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

// Custom hook to use the context
export function useCart() {
  return useContext(CartContext);
} 